<template>
  <div>
    <ValidationObserver ref="form" v-slot="{ invalid }">
      <v-overlay :value="loading" absolute>
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <div v-if="docs">
        <!-- Basic Information -->
        <v-subheader class="px-0">基本資料</v-subheader>
        <v-row dense>
          <v-col cols="12" md="6">
            <ValidationProvider v-slot="{ errors }" name="客戶名稱" rules="required">
              <v-text-field
                v-model="docs.name"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="客戶名稱 *"
                outlined
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider v-slot="{ errors }" name="電話">
              <v-text-field
                v-model="docs.phone"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="電話"
                outlined
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
        </v-row>

        <!-- Attachments -->
        <template v-if="!isNewRecord">
          <v-subheader class="px-0 mt-4">附件</v-subheader>
          <v-row dense>
            <v-col cols="12">
              <div class="d-flex align-center justify-space-between mb-3">
                <label class="v-label">附件檔案</label>
                <v-btn
                  small
                  color="primary"
                  :disabled="saving"
                  @click="$refs.attachmentInput && $refs.attachmentInput.click()"
                >
                  <v-icon small left>mdi-upload</v-icon>
                  上傳附件
                </v-btn>
              </div>

              <v-simple-table v-if="docs.attachments && docs.attachments.length > 0" dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>檔名</th>
                      <th class="text-right">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(attachment, index) in docs.attachments"
                      :key="'attachment-' + index"
                    >
                      <td>
                        <v-icon small left>mdi-file-document</v-icon>
                        {{ attachment.filename || attachment._id || attachment }}
                      </td>
                      <td class="text-right">
                        <v-btn
                          icon
                          small
                          color="primary"
                          :disabled="saving"
                          @click="openAttachment(attachment)"
                        >
                          <v-icon small>mdi-eye</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          color="error"
                          :disabled="saving"
                          @click="removeAttachment(index)"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <v-alert v-else type="info" outlined dense class="mt-2"> 尚未上傳任何附件 </v-alert>

              <!-- Hidden file input -->
              <input
                ref="attachmentInput"
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp"
                style="display: none"
                @change="handleAttachmentSelect"
                multiple
              />
            </v-col>
          </v-row>
        </template>
        <v-alert v-else type="info" outlined dense class="mt-4"> 建立客戶後才可上傳附件 </v-alert>

        <!-- Action Buttons -->
        <v-row dense class="mt-4">
          <v-col class="d-flex align-center gap-2">
            <v-btn :disabled="invalid || saving" :loading="saving" color="primary" @click="save">
              立即提交
            </v-btn>
            <v-spacer />

            <v-btn :disabled="saving" color="default" outlined @click="resetForm"> 重新填寫 </v-btn>

            <v-btn
              v-if="!isNewRecord"
              :disabled="saving"
              color="error"
              outlined
              @click="confirmDelete"
            >
              刪除
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import api from '@/api';
import { crudRead, crudUpdate } from '@/api/crud';
import { uploadSingleAttachment } from '@/api/attachment';
import { RESTFUL } from '@/data/constants';
import ApiError from '@/api/ApiError';

export default {
  name: 'CustomerDetailsForm',
  components: {},
  props: {
    id: {
      type: String,
      default: 'new',
    },
  },
  data() {
    return {
      docs: null,
      newAttachments: [],
      loading: false,
      saving: false,
    };
  },
  mounted() {
    this.reload();
  },
  computed: {
    isNewRecord() {
      return !this.id || this.id === 'new';
    },
  },
  methods: {
    async reload() {
      if (this.isNewRecord) {
        // Initialize new record with default values
        this.docs = {
          name: '',
          phone: '',
          type: 'client',
          attachments: [],
        };
      } else {
        this.loading = true;
        try {
          this.docs = await crudRead(api, RESTFUL.customer.read, this.id, {
            populates: [{ path: 'attachments', select: ['filename', '_id', 'contentType'] }],
          });

          // Ensure attachments is an array
          if (!this.docs.attachments) {
            this.docs.attachments = [];
          }
        } catch (e) {
          const ae = ApiError.wrap(e);
          await this.$store.dispatch('alert/updateAlertMessage', {
            msg: ae.message,
            type: 'error',
            color: 'error',
          });
        } finally {
          this.loading = false;
        }
      }
    },
    resetForm() {
      // Reset form validation
      this.$refs.form?.reset();
      // Reload data
      this.reload();
    },
    async handleAttachmentSelect(event) {
      if (this.isNewRecord) {
        // Guard: uploading is not allowed for new records
        event.target.value = '';
        return;
      }
      const files = Array.from(event.target.files);
      if (files.length) {
        this.newAttachments = files;
        await this.uploadAttachments();
        event.target.value = ''; // Reset input
      }
    },
    async uploadAttachments() {
      if (!this.newAttachments.length) {
        return;
      }
      if (this.isNewRecord) {
        return;
      }

      try {
        const attachments = await Promise.all(
          this.newAttachments.map(file => uploadSingleAttachment(api, file, this.id)),
        );

        const currentAttachments = Array.isArray(this.docs.attachments)
          ? this.docs.attachments
          : [];
        this.docs.attachments = [...currentAttachments, ...attachments];

        this.newAttachments = [];

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '附件已添加，請點擊儲存以保存變更',
          type: 'info',
          color: 'info',
        });
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      }
    },
    async removeAttachment(index) {
      const confirmed = await this.$root.$confirm.open('確認刪除', '確定要移除此附件嗎？', {
        color: 'red',
      });
      if (!confirmed) {
        return;
      }

      try {
        this.docs.attachments.splice(index, 1);

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '附件已移除，請點擊儲存以保存變更',
          type: 'info',
          color: 'info',
        });
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      }
    },
    /**
     * Open attachment in the dedicated viewer (same behavior as CRM for faster loading).
     * @param {object|string} attachment - Attachment object or attachment id
     */
    openAttachment(attachment) {
      const attachmentId =
        typeof attachment === 'object' && attachment ? attachment._id : attachment;
      const filename = typeof attachment === 'object' && attachment ? attachment.filename : '';
      if (!attachmentId) return;

      const route = this.$router.resolve({
        name: 'FileViewerPage',
        params: { attachmentId },
        query: { filename: filename || '文件' },
      });
      window.open(route.href, '_blank');
    },
    normalizeAttachmentIds(value) {
      if (!Array.isArray(value)) return [];
      return value.map(v => (typeof v === 'object' && v ? v._id : v)).filter(Boolean);
    },
    async confirmDelete() {
      const confirmed = await this.$root.$confirm.open(
        '確認刪除',
        '確定要刪除此記錄嗎？此操作無法復原。',
        {
          color: 'red',
        },
        '確定刪除',
      );
      if (!confirmed) {
        return;
      }
      this.deleteRecord();
    },
    async deleteRecord() {
      try {
        await api.delete(`${RESTFUL.customer.read}/${this.id}`);

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '刪除成功',
          type: 'success',
          color: 'success',
        });

        // Emit deleted event
        this.$emit('deleted');
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      }
    },
    async save() {
      this.saving = true;
      try {
        if (!(await this.$refs.form?.validate?.())) {
          this.saving = false;
          return;
        }

        // Prepare payload
        const payload = {
          name: this.docs.name,
          phone: this.docs.phone,
          type: 'client',
        };

        if (this.isNewRecord) {
          await api.post(RESTFUL.customer.create, payload);
        } else {
          payload.attachments = this.normalizeAttachmentIds(this.docs.attachments);
          await crudUpdate(api, RESTFUL.customer.read, this.id, payload);
        }

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '儲存成功',
          type: 'success',
          color: 'success',
        });

        // Emit saved event
        this.$emit('saved');
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
